import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAyats, deleteAyat, createMainPage, getMainPage, updateAyatStatus, getHeroImages, deleteHeroImage, getCelebrationConfig, updateCelebrationConfig } from './_request';
import CelebrationBannerPreview, { THEME_PALETTES } from './CelebrationBannerPreview';
import { IMAGE_URL } from '../constants/Consts';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Checkbox, Switch, FormControlLabel } from '@mui/material';

const FirstPage = () => {
  const [activeTab, setActiveTab] = useState('ayat');
  const [ayatData, setAyatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authToken } = useAuth();
  const navigate = useNavigate();
  const [jashans, setJashans] = useState([]);
  const [base64Image, setBase64Image] = useState(null);
  const [error, setError] = useState('');
  const IMAGE_LINK = `${IMAGE_URL}mainImage`;
  const [selectedAyatIds, setSelectedAyatIds] = useState([]);
  const [heroLibrary, setHeroLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // Celebration Banner State
  const [celebrationConfig, setCelebrationConfig] = useState({
    is_enabled: false,
    banner_text: 'جشنِ ولادت با سعادت مبارک',
    banner_title: 'جشنِ مسرت',
    duration_seconds: 8,
    theme_color: 'emerald',
    effect_type: 'sparkles_rain',
    show_frequency: 'every_visit',
  });
  const [loadingCelebration, setLoadingCelebration] = useState(false);
  const [savingCelebration, setSavingCelebration] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(false);

  const fetchCelebration = useCallback(async () => {
    try {
      setLoadingCelebration(true);
      const res = await getCelebrationConfig(authToken);
      if (res && res.success && res.data) {
        setCelebrationConfig(res.data);
      }
    } catch (e) {
      console.error("Error fetching celebration config:", e);
    } finally {
      setLoadingCelebration(false);
    }
  }, [authToken]);

  const handleSaveCelebration = async () => {
    try {
      setSavingCelebration(true);
      const res = await updateCelebrationConfig(authToken, celebrationConfig);
      if (res && res.success) {
        alert("Celebration banner configuration updated successfully!");
        if (res.data) {
          setCelebrationConfig(res.data);
        }
      } else {
        alert(res?.message || "Failed to update celebration configuration");
      }
    } catch (err) {
      console.error("Error saving celebration config:", err);
      alert("Error saving celebration configuration");
    } finally {
      setSavingCelebration(false);
    }
  };

  const fetchHeroLibrary = useCallback(async () => {
    try {
      setLoadingLibrary(true);
      const res = await getHeroImages(authToken);
      if (res && res.success) {
        setHeroLibrary(res.data || []);
      }
    } catch (e) {
      console.error("Error fetching hero library:", e);
    } finally {
      setLoadingLibrary(false);
    }
  }, [authToken]);

  const handleSelectFromLibrary = (imageName) => {
    setBase64Image(imageName);
  };

  const handleDeleteFromLibrary = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this image from the library?")) return;
    try {
      const res = await deleteHeroImage(authToken, id);
      if (res.success) {
        alert("Image deleted successfully");
        fetchHeroLibrary();
      } else {
        alert(res.message || "Failed to delete image");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting image");
    }
  };


const handleCheckboxChange = (id) => {
    setSelectedAyatIds((prev) =>
      prev.includes(id)
        ? prev.filter((ayatId) => ayatId !== id) // Uncheck: remove ID
        : [...prev, id] // Check: add ID
    );
  };
  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const img = new Image();
    const reader = new FileReader();

    reader.onloadend = () => {
      img.onload = () => {
        if (img.width >= 1312 && img.height >= 450) {
          setBase64Image(reader.result);
          setError('');
        } else {
          setError('Image must be at least 1312px wide and 450px tall.');
          setBase64Image(null);
        }
      };
      img.src = reader.result;
    };

    reader.readAsDataURL(file);
  };

  const handleJashanBannerChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const img = new Image();
    const reader = new FileReader();

    reader.onloadend = () => {
      img.onload = () => {
        if (img.width >= 300 && img.height >= 150) {
          const updated = [...jashans];
          updated[index].bannerImage = reader.result;
          setJashans(updated);
          setError('');
        } else {
          setError('Banner image must be at least 800px wide and 200px tall.');
        }
      };
      img.src = reader.result;
    };

    reader.readAsDataURL(file);
  };

  const addJashan = () => {
    setJashans([
      ...jashans,
      {
        location: '',
        title: '',
        date: '',
        time: '',
        description: '',
        bannerImage: null,
      },
    ]);
  };

  const handleJashanChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...jashans];
    updated[index][name] = value;
    setJashans(updated);
  };

  const removeJashan = (index) => {
    const updated = [...jashans];
    updated.splice(index, 1);
    setJashans(updated);
  };

  const getAyatsList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAyats(authToken);
      if (response?.Result) {
        setAyatData(response?.Data || []);
      } else {
        setAyatData([]);
      }
    } catch (error) {
      console.error("Failed to fetch Ayats:", error);
      setAyatData([]);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Ayat?")) return;

    try {
      const response = await deleteAyat(authToken, id);
      if (response?.Result) {
        alert("Ayat deleted successfully.");
        getAyatsList();
      } else {
        alert("Failed to delete Ayat.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Error deleting Ayat.");
    }
  };

  const getMain = useCallback(async () => {
    try {
      const response = await getMainPage(authToken);
      if (response.success) {
        setJashans(response.data.jashans || []);
        setBase64Image(response.data.heroImage);
      }
    } catch (error) {
      console.error("Error fetching main page data:", error);
    }
  }, [authToken]);

  useEffect(() => {
    if (activeTab === 'ayat') {
      getAyatsList();
    } else if (activeTab === 'hero') {
      getMain();
      fetchHeroLibrary();
    } else if (activeTab === 'jashan') {
      getMain();
    } else if (activeTab === 'celebration') {
      fetchCelebration();
    }
  }, [activeTab, getAyatsList, getMain, fetchHeroLibrary, fetchCelebration]);

  const handleEdit = (id) => {
    navigate('/home/editAyat', { state: { id } });
  };

  const CreateUpdateMain = async () => {
    try {
      const payload = {
        heroImage: base64Image,
        jashans,
      };

      const response = await createMainPage(authToken, payload);

      if (response.success) {
        alert("Main page data saved successfully");
        if (activeTab === 'hero') {
          fetchHeroLibrary();
          getMain();
        }
      } else {
        console.error("Failed to save main page data:", response.message);
      }

      return response;
    } catch (error) {
      console.error("Error in CreateUpdateMain:", error);
      return { success: false, message: "Something went wrong" };
    }
  };
const handleSaveSelectedAyats = async (newStatus) => {
  try {
    const response = await updateAyatStatus(authToken, selectedAyatIds, newStatus);
    if (response.Result) {
      alert(response.Msg);
      getAyatsList(); // Refresh the Ayat list to reflect updated statuses
    } else {
      alert(response.Msg);
    }
  } catch (error) {
    console.error('Error saving selected Ayat IDs:', error);
    alert('Error updating Ayat status.');
  }
};
  return (
    <div className="p-4">
      {/* Live Preview Modal Overlay */}
      {showLivePreview && (
        <CelebrationBannerPreview
          bannerText={celebrationConfig.banner_text || 'جشنِ ولادت با سعادت مبارک'}
          bannerTitle={celebrationConfig.banner_title || 'جشنِ مسرت'}
          themeColor={celebrationConfig.theme_color || 'emerald'}
          effectType={celebrationConfig.effect_type || 'sparkles_rain'}
          durationSeconds={celebrationConfig.duration_seconds || 8}
          isPreviewMode={true}
          onClose={() => setShowLivePreview(false)}
        />
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeTab === 'ayat' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('ayat')}
          color={activeTab === 'ayat' ? 'primary' : 'inherit'}
        >
          Ayat o Rewayat
        </Button>
        <Button
          variant={activeTab === 'hero' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('hero')}
          color={activeTab === 'hero' ? 'primary' : 'inherit'}
        >
          Hero Image
        </Button>
        <Button
          variant={activeTab === 'jashan' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('jashan')}
          color={activeTab === 'jashan' ? 'primary' : 'inherit'}
        >
          Jashan e Noor
        </Button>
        <Button
          variant={activeTab === 'celebration' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('celebration')}
          color={activeTab === 'celebration' ? 'secondary' : 'inherit'}
          sx={{
            fontWeight: 'bold',
            ...(activeTab === 'celebration' ? {} : { borderColor: '#d97706', color: '#b45309' })
          }}
        >
          ✨ Celebration Banner (جشن و مبارکباد)
        </Button>
      </div>

      <div className="mt-4 text-lg font-semibold">
        {activeTab === 'ayat' ? (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Ayat o Rewayat</h2>
              <div className=' flex gap-2'>

             <Button
      variant="contained"
      color="primary"
      onClick={() => handleSaveSelectedAyats('Active')} // Set selected Ayats to Active
      disabled={loading || selectedAyatIds.length === 0}
    >
      Set Active
    </Button>
    <Button
      variant="contained"
      color="primary"
      onClick={() => handleSaveSelectedAyats('Not Active')} // Set selected Ayats to Not Active
      disabled={loading || selectedAyatIds.length === 0}
    >
      Set Not Active
    </Button>
    <Button
      variant="contained"
      color="primary"
      onClick={() => navigate('/home/addAyat')}
      disabled={loading}
    >
      Add Ayat
    </Button>
               </div>
            </div>

            <div className="bg-white shadow rounded-lg overflow-x-auto">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '16px' }}><CircularProgress /></div>
              ) : (
                <table className="min-w-full table-auto text-left">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 border">
                        <Checkbox
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAyatIds(ayatData.map((item) => item.id));
                            } else {
                              setSelectedAyatIds([]);
                            }
                          }}
                          checked={
                            ayatData.length > 0 &&
                            selectedAyatIds.length === ayatData.length
                          }
                        />
                      </th>
                      <th className="px-4 py-2 border">Ayat</th>
                      <th className="px-4 py-2 border">Status</th>
                      <th className="px-4 py-2 border">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ayatData.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border">
                          <Checkbox
                            checked={selectedAyatIds.includes(item.id)}
                            onChange={() => handleCheckboxChange(item.id)}
                          />
                        </td>
                        <td className="px-4 py-2 border">{item.title || '—'}</td>
                        <td className="px-4 py-2 border">{item.status || '—'}</td>
                        <td className="px-4 py-2 border space-x-2">
                          <Button
                            variant="text"
                            color="primary"
                            onClick={() => handleEdit(item.id)}
                            disabled={loading}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            color="error"
                            onClick={() => handleDelete(item.id)}
                            disabled={loading}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {ayatData.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center px-4 py-4 text-gray-500">
                          No Ayat found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : activeTab === 'hero' ? (
          <div className="p-6 max-w-5xl mx-auto bg-white rounded shadow-md space-y-8">
            <div className="flex justify-between items-center border-b pb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Hero Image Configuration</h2>
                <p className="text-sm text-gray-500 mt-1">Upload a new hero banner or select from previously uploaded images.</p>
              </div>
              <Button
                variant="contained"
                color="success"
                onClick={CreateUpdateMain}
                disabled={loading}
              >
                Save Changes
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Upload & Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700">Upload New Banner</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 hover:bg-gray-100/50 transition">
                  <input
                    type="file"
                    id='heroImage'
                    accept=".jpg, .jpeg, .png"
                    onChange={handleHeroImageChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                  <p className="text-xs text-gray-400 mt-2">Recommended resolution: at least 1312px width × 450px height. Formats: JPG, PNG, JPEG.</p>
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>

                {base64Image && (
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Selected Banner Preview:</p>
                    <div className="relative rounded overflow-hidden shadow-md bg-white">
                      <img
                        src={base64Image?.startsWith('data:image') ? base64Image : `${IMAGE_LINK}/${base64Image}`}
                        alt="Hero Preview"
                        className="w-full max-h-[300px] object-cover"
                      />
                      <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow">
                        Active Selection (Unsaved until saved changes)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Library Gallery */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700">Previously Uploaded Banners</h3>
                {loadingLibrary ? (
                  <div className="flex justify-center py-8"><CircularProgress size={30} /></div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-1">
                    {heroLibrary.map((item) => {
                      const isCurrentActive = base64Image === item.imageName;
                      return (
                        <div
                          key={item.id}
                          onClick={() => handleSelectFromLibrary(item.imageName)}
                          className={`relative border rounded-xl overflow-hidden cursor-pointer bg-white group shadow-sm hover:shadow-md transition-all duration-200 ${isCurrentActive ? 'ring-2 ring-blue-500 border-transparent scale-[0.98]' : 'border-gray-200'}`}
                        >
                          <img
                            src={`${IMAGE_LINK}/${item.imageName}`}
                            alt="Library Item"
                            className="w-full h-24 object-cover"
                          />
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition duration-150 flex items-center justify-center">
                            <span className="text-white text-xs font-semibold bg-blue-600 px-3 py-1.5 rounded-full shadow">Select Image</span>
                          </div>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={(e) => handleDeleteFromLibrary(item.id, e)}
                            className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full shadow opacity-0 group-hover:opacity-100 transition duration-150"
                            title="Delete from Library"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>

                          {/* Active Indicator Checkmark */}
                          {isCurrentActive && (
                            <div className="absolute top-1 left-1 bg-blue-600 text-white p-1 rounded-full shadow">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {heroLibrary.length === 0 && (
                      <p className="text-gray-500 text-xs text-center py-8 col-span-2">No previously uploaded banners found.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'jashan' ? (
          <div className="p-6 max-w-3xl mx-auto bg-white rounded shadow-md space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold">Jashan e Noor</h2>
              <Button
                variant="contained"
                color="success"
                onClick={CreateUpdateMain}
                disabled={loading}
              >
                Save Changes
              </Button>
            </div>

            <Button
              variant="contained"
              color="success"
              onClick={addJashan}
              disabled={loading}
            >
              Add Jashan e Noor
            </Button>

            {jashans.map((jashan, index) => (
              <div key={index} className="border p-4 rounded-md mt-4 space-y-3 bg-gray-50">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-lg">Jashan #{index + 1}</h3>
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => removeJashan(index)}
                    disabled={loading}
                  >
                    Remove
                  </Button>
                </div>

                <div>
                  <label className="block font-medium">Banner Image</label>
                  <input
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    onChange={(e) => handleJashanBannerChange(index, e)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  {jashan.bannerImage && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-1">Banner Preview:</p>
                      <img
                        src={jashan.bannerImage?.startsWith('data:image') ? jashan.bannerImage : `${IMAGE_LINK}/${jashan.bannerImage}`}
                        alt="Banner Preview"
                        className="max-w-sm border rounded"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block font-medium">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={jashan.location}
                    onChange={(e) => handleJashanChange(index, e)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block font-medium">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={jashan.title}
                    onChange={(e) => handleJashanChange(index, e)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block font-medium">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={jashan.date}
                    onChange={(e) => handleJashanChange(index, e)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block font-medium">Time</label>
                  <input
                    type="time"
                    name="time"
                    value={jashan.time}
                    onChange={(e) => handleJashanChange(index, e)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block font-medium">Description</label>
                  <textarea
                    name="description"
                    value={jashan.description}
                    onChange={(e) => handleJashanChange(index, e)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md"
                  ></textarea>
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === 'celebration' ? (
          <div className="p-6 max-w-4xl mx-auto bg-white rounded-xl shadow-md border space-y-8">
            {/* Header & Save Action */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b pb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <span>✨ Celebration & Cloth Banner Settings</span>
                  <span className="text-sm font-normal text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                    جشن و مبارکباد
                  </span>
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configure the celebratory sparkle rain effect and realistic waving cloth banner displayed when users visit the website.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowLivePreview(true)}
                  startIcon={<span>👁️</span>}
                >
                  Live Preview
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSaveCelebration}
                  disabled={savingCelebration || loadingCelebration}
                >
                  {savingCelebration ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>

            {loadingCelebration ? (
              <div className="flex justify-center py-12">
                <CircularProgress />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Enable / Disable Switch */}
                <div className={`p-4 rounded-xl border transition-all ${celebrationConfig.is_enabled ? 'bg-emerald-50/70 border-emerald-300' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800 text-base">
                        Celebration Effect & Banner Status
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Turn this ON to show the celebration sparkles rain and waving cloth banner on the first page.
                      </p>
                    </div>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(celebrationConfig.is_enabled)}
                          onChange={(e) =>
                            setCelebrationConfig({ ...celebrationConfig, is_enabled: e.target.checked })
                          }
                          color="success"
                        />
                      }
                      label={
                        <span className={`text-sm font-bold ${celebrationConfig.is_enabled ? 'text-emerald-700' : 'text-gray-500'}`}>
                          {celebrationConfig.is_enabled ? 'ACTIVE (آن ہے)' : 'DISABLED (بند ہے)'}
                        </span>
                      }
                    />
                  </div>
                </div>

                {/* Banner Text Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Main Celebration Sentence (مبارکباد کا بنیادی جملہ) *
                    </label>
                    <input
                      type="text"
                      dir="rtl"
                      value={celebrationConfig.banner_text || ''}
                      onChange={(e) =>
                        setCelebrationConfig({ ...celebrationConfig, banner_text: e.target.value })
                      }
                      placeholder="مثال: جشنِ ولادت با سعادت مبارک یا عید مبارک"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-right font-nastaleeqRegular focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-sm"
                    />
                    <p className="text-xs text-gray-400">
                      This text will appear in large calligraphy across the waving cloth banner.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Banner Subtitle / Occasion (عنوان یا مناسبت)
                    </label>
                    <input
                      type="text"
                      dir="rtl"
                      value={celebrationConfig.banner_title || ''}
                      onChange={(e) =>
                        setCelebrationConfig({ ...celebrationConfig, banner_title: e.target.value })
                      }
                      placeholder="مثال: جشنِ مسرت، عید میلاد النبی ﷺ"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Display Duration (سیکنڈز میں دورانیہ)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="3"
                        max="60"
                        value={celebrationConfig.duration_seconds || 8}
                        onChange={(e) =>
                          setCelebrationConfig({ ...celebrationConfig, duration_seconds: parseInt(e.target.value) || 8 })
                        }
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-sm"
                      />
                      <span className="text-sm text-gray-600">seconds (recommended: 6 - 10s)</span>
                    </div>
                  </div>
                </div>

                {/* Cloth Theme & Palette Selection */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700">
                    Banner Cloth Theme & Color Palette (کپڑے اور بینر کا رنگ)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(THEME_PALETTES).map(([key, theme]) => {
                      const isSelected = (celebrationConfig.theme_color || 'emerald') === key;
                      return (
                        <div
                          key={key}
                          onClick={() => setCelebrationConfig({ ...celebrationConfig, theme_color: key })}
                          className={`p-3 rounded-xl cursor-pointer border-2 transition-all flex items-center gap-3 bg-white hover:shadow-md ${
                            isSelected ? 'border-emerald-600 ring-2 ring-emerald-400/40 bg-emerald-50/30' : 'border-gray-200'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${theme.bgGradient} border border-amber-300 shadow-sm flex-shrink-0`} />
                          <div className="overflow-hidden">
                            <p className="text-xs font-bold text-gray-800 truncate">{theme.name}</p>
                            <p className="text-[10px] text-gray-500">{key}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Particle Effect Selection */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700">
                    Celebration Particle Effect (جشن کا انداز / افیکٹ)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { id: 'sparkles_rain', label: 'Google Sparkles Rain (سنہرے ستارے)', desc: 'Shimmering gold stars and glowing light sparkles' },
                      { id: 'confetti', label: 'Festive Confetti (رنگ برنگی بارش)', desc: 'Colorful ribbons and festive confetti paper' },
                      { id: 'golden_stars', label: 'Starlight Glitter (نورانی کرنیں)', desc: 'Gentle twinkling stars and golden dust' },
                    ].map((eff) => {
                      const isSelected = (celebrationConfig.effect_type || 'sparkles_rain') === eff.id;
                      return (
                        <div
                          key={eff.id}
                          onClick={() => setCelebrationConfig({ ...celebrationConfig, effect_type: eff.id })}
                          className={`p-3.5 rounded-xl cursor-pointer border-2 transition-all ${
                            isSelected ? 'border-amber-500 bg-amber-50/40 ring-2 ring-amber-300' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="text-xs font-bold text-gray-800">{eff.label}</p>
                          <p className="text-[11px] text-gray-500 mt-1">{eff.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Live In-Page Mini Preview Card */}
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Static Preview Simulation
                    </h4>
                    <button
                      type="button"
                      onClick={() => setShowLivePreview(true)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline"
                    >
                      Click to Test Fullscreen Animation (Sparkles + Wave)
                    </button>
                  </div>

                  <div className={`relative p-6 rounded-2xl bg-gradient-to-r ${(THEME_PALETTES[celebrationConfig.theme_color] || THEME_PALETTES.emerald).bgGradient} border-2 ${(THEME_PALETTES[celebrationConfig.theme_color] || THEME_PALETTES.emerald).bannerBorder} shadow-lg text-center overflow-hidden`}>
                    <div className="absolute inset-1 border border-dashed border-amber-300/30 rounded-xl pointer-events-none" />
                    {celebrationConfig.banner_title && (
                      <span className="inline-block text-xs text-amber-300 bg-amber-400/20 px-3 py-0.5 rounded-full mb-2 border border-amber-300/30">
                        ★ {celebrationConfig.banner_title} ★
                      </span>
                    )}
                    <h3
                      dir="rtl"
                      className="text-2xl md:text-3xl font-bold text-amber-100 drop-shadow font-nastaleeqRegular leading-relaxed"
                    >
                      {celebrationConfig.banner_text || 'جشنِ ولادت با سعادت مبارک'}
                    </h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FirstPage;